import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { useRef } from "react";

function VerifyCertificate() {
  const [certificateId, setCertificateId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API = process.env.REACT_APP_API_URL;
  const { id } = useParams();
  const navigate = useNavigate();
  const hasPrefilledFromUrl = useRef(false);
  const handleVerify = useCallback(
    async (idFromAuto = null) => {
      const certId = idFromAuto || certificateId;

      if (!certId.trim()) {
        alert("Please enter a Certificate ID");
        return;
      }

      setLoading(true);
      setError(null);
      setResult(null);

      try {
        // ✅ FIXED API PATH (NO DOUBLE /api)
        const res = await fetch(`${API}/api/certificates/verify/${certId}`);

        const data = await res.json();

        if (!res.ok || !data.success) {
          setError(data.message || "Invalid certificate");
          return;
        }

        setResult(data.certificate);
        navigate(`/verify/${certId}`, { replace: true });
      } catch (err) {
        console.error("Verification error:", err);
        setError("Server error. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [API, certificateId, navigate]
  );

  useEffect(() => {
    if (id && !hasPrefilledFromUrl.current) {
      setCertificateId(id);
      handleVerify(id);
      hasPrefilledFromUrl.current = true;
    }
  }, [id, handleVerify]);

  return (
    <Layout>
      <div className="page-container">
        <div className="page-header reveal reveal-up">
          <div className="page-badge">
            <span className="float-soft" style={{ display: 'inline-block' }}>🔐</span> Verification Portal
          </div>
          <h1 className="page-title">
            <span className="gradient-text">Verify Certificate</span>
          </h1>
          <p className="page-subtitle">
            Enter the Certificate ID to verify authenticity and confirm your credentials
          </p>
        </div>

        <div className="card reveal reveal-up stagger-2 shimmer-border" style={{ position: 'relative' }}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleVerify();
            }}
          >
            <div className="form-group">
              <label className="form-label">Certificate UID</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter your Certificate UID (e.g., SYN-XXXX-XXXX)"
                value={certificateId}
                onChange={(e) => {
                  setCertificateId(e.target.value);
                  if (e.target.value === "") {
                    setResult(null);
                    setError(null);
                    navigate("/verify", { replace: true });
                  }
                }}
                disabled={loading}
              />
            </div>

            <button className="btn btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <span className="loading-spinner" /> Verifying…
                </>
              ) : (
                "🔍 Verify Certificate"
              )}
            </button>
          </form>

          {error && (
            <div className="result-card error mt-3">
              <div className="result-icon">❌</div>
              <p style={{ fontWeight: 600, color: '#dc2626' }}>{error}</p>
            </div>
          )}

          {result && (
            <div className="result-card success mt-3">
              <div className="result-icon" style={{ animation: 'floatSoft 2s ease-in-out infinite' }}>✅</div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1rem' }}>
                <span style={{ background: 'linear-gradient(135deg, #059669, #10b981)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Certificate Verified Successfully
                </span>
              </h2>

              <div className="result-details">
                {[
                  { label: 'Name', value: result.name },
                  { label: 'Domain', value: result.domain },
                  { label: 'Batch', value: result.batch },
                  { label: 'Issued On', value: new Date(result.certificate_issued_at).toLocaleDateString() },
                  { label: 'Certificate ID', value: result.certificate_id },
                ].map((row, i) => (
                  <div className="detail-row" key={row.label} style={{ animationDelay: `${i * 80}ms` }}>
                    <span>{row.label}:</span>
                    <span>{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Trust indicators */}
        <div className="reveal reveal-up stagger-3" style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
          {['🔒 Tamper-Proof', '⚡ Instant Verification', '🌐 Globally Valid'].map((item) => (
            <div key={item} style={{
              fontSize: '0.8rem',
              color: '#64748b',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              padding: '0.375rem 0.75rem',
              borderRadius: '9999px',
              background: 'rgba(255,255,255,.6)',
              border: '1px solid rgba(226,232,240,.5)',
              backdropFilter: 'blur(8px)',
            }}>
              {item}
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}

export default VerifyCertificate;
